/* PimziKids video showcase.
   Clips load only when the section comes near, play silently while a card is on screen,
   and pause when it leaves. Never plays with sound unless the visitor turns sound on.
   With reduced motion or data saver on, nothing plays until the visitor presses play. */
(() => {
  if (window.__pkShowcase) return;
  window.__pkShowcase = true;

  const rmq = matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = !!(navigator.connection && navigator.connection.saveData);
  const canAutoplay = () => !rmq.matches && !saveData;
  const all = [];

  function init(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const track = root.querySelector('.sc-track');
    const cards = [...root.querySelectorAll('.sc-card')];
    const prev = root.querySelector('.sc-prev'), next = root.querySelector('.sc-next');
    const autoplay = root.dataset.autoplay !== 'false';
    const video = card => card.querySelector('video');
    const label = card => card.dataset.label || 'video';

    function load(v) {
      if (v.dataset.src && !v.getAttribute('src')) { v.preload = 'metadata'; v.src = v.dataset.src; }
    }
    function playUI(card) {
      const playing = !video(card).paused;
      card.classList.toggle('is-playing', playing);
      card.querySelector('.sc-play').setAttribute('aria-label', (playing ? 'Pause video: ' : 'Play video: ') + label(card));
    }
    function soundUI(card) {
      const b = card.querySelector('.sc-mute');
      if (!b) return;
      const on = !video(card).muted;
      card.classList.toggle('is-sound', on);
      b.setAttribute('aria-label', (on ? 'Turn sound off: ' : 'Turn sound on: ') + label(card));
    }
    function play(card) {
      const v = video(card);
      load(v);
      const p = v.play();
      if (p && p.catch) p.catch(() => playUI(card));
    }
    function silence(card) { const v = video(card); if (!v.muted) { v.muted = true; soundUI(card); } }

    cards.forEach(card => {
      const v = video(card);
      card.s = { visible: false, userPaused: false, byUser: false };
      v.muted = true;
      v.addEventListener('play', () => playUI(card));
      v.addEventListener('pause', () => playUI(card));
      const toggle = () => {
        if (v.paused) { card.s.userPaused = false; card.s.byUser = true; play(card); }
        else { card.s.userPaused = true; v.pause(); }
      };
      card.querySelector('.sc-play').addEventListener('click', toggle);
      v.addEventListener('click', toggle);
      const m = card.querySelector('.sc-mute');
      if (m) m.addEventListener('click', () => {
        const turnOn = v.muted;
        // only one clip plays sound at a time, across every showcase on the page
        if (turnOn) all.forEach(c => c !== card && silence(c));
        v.muted = !turnOn;
        soundUI(card);
        if (turnOn && v.paused) { card.s.userPaused = false; card.s.byUser = true; play(card); }
      });
      all.push(card);
    });

    // fetch clips only once the section is close to the screen
    const near = new IntersectionObserver(es => {
      if (!es.some(e => e.isIntersecting)) return;
      near.disconnect();
      if (canAutoplay() && autoplay) cards.forEach(c => load(video(c)));
    }, { rootMargin: '400px 0px' });
    near.observe(root);

    // play silently while a card is mostly visible; pause and re-mute it when it leaves
    const seen = new IntersectionObserver(es => es.forEach(e => {
      const card = e.target, v = video(card);
      card.s.visible = e.intersectionRatio >= 0.6;
      if (card.s.visible) {
        if (autoplay && canAutoplay() && !card.s.userPaused && v.paused) { silence(card); card.s.byUser = false; play(card); }
      } else if (!v.paused) {
        v.pause();
        silence(card);
      }
    }), { threshold: [0, 0.6] });
    cards.forEach(c => seen.observe(c));

    // arrows appear only when there are more videos than fit
    const gap = () => parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = () => (cards[0] ? cards[0].getBoundingClientRect().width + gap() : track.clientWidth);
    function nav() {
      const max = track.scrollWidth - track.clientWidth;
      const more = max > 4;
      root.classList.toggle('has-more', more);
      [[prev, track.scrollLeft <= 4], [next, track.scrollLeft >= max - 4]].forEach(([b, end]) => {
        if (!b) return;
        b.hidden = !more;
        b.setAttribute('aria-disabled', String(end));
      });
    }
    function go(dir, b) {
      if (b.getAttribute('aria-disabled') === 'true') return;
      const cs = getComputedStyle(track);
      const inner = track.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const per = Math.max(1, Math.floor((inner + gap() / 2) / step()));
      track.scrollBy({ left: dir * per * step(), behavior: rmq.matches ? 'auto' : 'smooth' });
    }
    prev && prev.addEventListener('click', () => go(-1, prev));
    next && next.addEventListener('click', () => go(1, next));
    let raf = 0;
    track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(nav); }, { passive: true });
    addEventListener('resize', nav);
    nav();
  }

  // reduced motion switched on: stop the clips that started by themselves
  rmq.addEventListener('change', () => {
    if (rmq.matches) all.forEach(c => { if (!c.s.byUser) c.querySelector('video').pause(); });
  });
  document.addEventListener('visibilitychange', () => {
    all.forEach(c => {
      const v = c.querySelector('video');
      if (document.hidden) { if (!v.paused) { v.pause(); c.s.hiddenPause = true; } }
      else if (c.s.hiddenPause) { c.s.hiddenPause = false; if (c.s.visible) v.play().catch(() => {}); }
    });
  });

  const start = scope => scope.querySelectorAll('[data-showcase]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start(document));
  else start(document);
  document.addEventListener('shopify:section:load', e => start(e.target));
})();
